import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { ipcCall } from '@/composables/useIPC'
import { getPreference, setPreference } from '@/composables/usePreference'
import { IpcCommand } from '@/types/ipc'

interface UpdateInfo {
  version: string
  releaseNotes?: string
  pubDate?: string
  isForced?: boolean
}

interface UpdateProgress {
  percent: number
}

const AUTO_UPDATE_CHECK_COOLDOWN_MS = 4 * 60 * 60 * 1000
const LAST_AUTO_CHECK_KEY = 'update_last_auto_check_at'

export const useUpdateStore = defineStore('update', () => {
  const availableInfo = ref<UpdateInfo | null>(null)
  const checking = ref(false)
  const pendingForceCheck = ref(false)
  let pendingManualCheck = false
  let activeCheckPromise: Promise<UpdateInfo | null> | null = null
  const installing = ref(false)
  const progress = ref(0)
  const manualPresentationRevision = ref(0)
  const lastAutoCheckAt = ref(getPreference<number>(LAST_AUTO_CHECK_KEY) || 0)

  const hasUpdate = computed(() => availableInfo.value !== null)
  const version = computed(() => availableInfo.value?.version || '')
  const isForcedUpdate = computed(() => !!availableInfo.value?.isForced)

  function markAutoChecked() {
    const now = Date.now()
    lastAutoCheckAt.value = now
    setPreference(LAST_AUTO_CHECK_KEY, now)
  }

  function setAvailable(info: UpdateInfo) {
    availableInfo.value = {
      ...info,
      isForced: !!info.isForced,
    }
  }

  function clearAvailable() {
    availableInfo.value = null
    progress.value = 0
  }

  function setProgress(payload: UpdateProgress) {
    progress.value = Math.max(0, Math.min(100, Math.round(payload.percent || 0)))
  }

  async function executeCheck(manual: boolean, clearWhenEmpty: boolean) {
    try {
      const info = await ipcCall<UpdateInfo | null>(IpcCommand.UpdateCheck, { manual })
      if (info) {
        setAvailable(info)
      } else if (clearWhenEmpty) {
        clearAvailable()
      }
      return info
    } catch (error) {
      if (clearWhenEmpty) clearAvailable()
      throw error
    }
  }

  async function checkForUpdate(options: { manual?: boolean; force?: boolean } = {}) {
    const manual = !!options.manual
    const force = !!options.force
    if (checking.value) {
      if (force || manual) pendingForceCheck.value = true
      if (manual) pendingManualCheck = true
      const result = await (activeCheckPromise ?? Promise.resolve(availableInfo.value))
      if (manual && result) manualPresentationRevision.value += 1
      return result
    }

    if (!manual) {
      const now = Date.now()
      if (!force && now - lastAutoCheckAt.value < AUTO_UPDATE_CHECK_COOLDOWN_MS) {
        return null
      }
      markAutoChecked()
    }

    checking.value = true
    pendingManualCheck = false
    activeCheckPromise = (async () => {
      let result: UpdateInfo | null = null
      let failure: unknown
      let nextManual = manual
      let clearWhenEmpty = manual || force
      while (true) {
        pendingForceCheck.value = false
        try {
          result = await executeCheck(nextManual, clearWhenEmpty)
          failure = undefined
        } catch (error) {
          failure = error
        }

        if (!pendingForceCheck.value) break
        const queuedManual = pendingManualCheck
        pendingManualCheck = false
        markAutoChecked()
        nextManual = queuedManual
        clearWhenEmpty = true
      }
      if (failure !== undefined) throw failure
      return result
    })()
    try {
      const result = await activeCheckPromise
      if (manual && result) manualPresentationRevision.value += 1
      return result
    } finally {
      activeCheckPromise = null
      checking.value = false
      pendingForceCheck.value = false
      pendingManualCheck = false
    }
  }

  async function installUpdate() {
    if (installing.value) return

    const preserveForcedCandidate = isForcedUpdate.value
    installing.value = true
    progress.value = 0
    try {
      await ipcCall(IpcCommand.UpdateInstall)
      clearAvailable()
    } catch (e) {
      if (!preserveForcedCandidate) clearAvailable()
      throw e
    } finally {
      installing.value = false
    }
  }

  return {
    availableInfo,
    checking,
    installing,
    progress,
    manualPresentationRevision,
    hasUpdate,
    version,
    isForcedUpdate,
    setAvailable,
    clearAvailable,
    setProgress,
    checkForUpdate,
    installUpdate,
  }
})
